import React from 'react';
import { Button } from '../components/ui/button';
import { LogOut, Users, FileText, AlertCircle, Shield, DollarSign, Crown, Briefcase, Receipt, Building2, Printer } from 'lucide-react';
import PremiumHeader from '../components/brand/PremiumHeader';
import BackButton from '../components/BackButton';
import PrintInstitutionalFrame from '../components/PrintInstitutionalFrame';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';

export function AdminDashboardHeaderBar({ t, user, handleLogout }) {
  return (
    <>
      <PrintInstitutionalFrame
        docTitle="Tableau de Bord Administration"
        caption="Centre de gestion"
        captionSecondary={user?.role === 'super_admin' ? 'Super Administrateur' : 'Administrateur'}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <BackButton />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-coppam-blue truncate">{t('admin_dashboard.title', 'Administration')}</h1>
                <p className="text-sm text-slate-600 truncate">
                  {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'} - <span className="font-semibold text-coppam-gold">{user?.nom} {user?.prenom}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <NotificationBell />
              <LanguageSelector variant="compact" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => { try { window.print(); } catch {} }}
                className="border-amber-300/60 text-amber-700 hover:bg-amber-50 print-allow"
                title="Imprimer ou exporter en PDF institutionnel (Ctrl+P)"
                data-testid="print-institutional-btn"
              >
                <Printer className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Imprimer PDF officiel</span>
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

export function AdminDashboardStats({ t, stats, isSuperAdmin, navigate, setActiveTab }) {
  return (
    <>
      {/* === HEADER PREMIUM O'SCOP — Logo officiel + Navy & Or === */}
      <PremiumHeader
        eyebrow="Centre de gestion"
        title="Tableau de Bord Administration"
        subtitle="Pilotage unifié de la plateforme — Membres, COOPER'S, souscriptions & finance."
        testId="admin-header-premium"
        className="mb-8"
        actions={
          isSuperAdmin && (
            <Button
              onClick={() => navigate('/admin/super-dashboard')}
              className="group bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-semibold shadow-lg shadow-[#DEA900]/25 border-0"
              data-testid="super-admin-dashboard-btn"
            >
              <Crown className="h-4 w-4 mr-2 transition-transform group-hover:rotate-12" />
              Super Admin Dashboard
            </Button>
          )
        }
      />
      {/* KPI Stats — version premium glass-morphism (hors PremiumHeader pour conserver les data-testid) */}
      <div className="relative -mt-6 mb-8 mx-3 sm:mx-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 bg-[#0a3d62] rounded-xl p-3 shadow-lg shadow-[#0a3d62]/30 border border-[#DEA900]/15">
        {[
          { key: 'membres', label: t('admin_dashboard.members', 'Membres'), val: stats?.membres?.total || 0, icon: Users, tab: 'gestion-membres-cooper', accent: false },
          { key: 'coopers', label: "COOPER'S", val: stats?.coopers?.total || 0, icon: Shield, tab: 'coopers', accent: true },
          { key: 'experts', label: "COOPER'S Advisors", val: stats?.experts?.total || 0, icon: Briefcase, tab: 'experts-validation', accent: false },
          { key: 'subs', label: t('admin_dashboard.subscriptions', 'Souscriptions'), val: stats?.souscriptions?.total || 0, icon: FileText, tab: 'souscriptions-liste', accent: false },
          { key: 'dommages', label: t('admin_dashboard.damages', 'Dommages'), val: stats?.dommages?.total || 0, icon: AlertCircle, tab: null, accent: false },
          { key: 'paiements', label: t('admin_dashboard.payments', 'Paiements'), val: stats?.souscriptions?.en_attente_paiement || 0, icon: DollarSign, tab: 'souscriptions-validation', accent: false },
        ].map(({ key, label, val, icon: Ico, tab, accent }) => {
          const clickable = !!tab;
          return (
            <button
              key={key}
              type="button"
              onClick={() => tab && setActiveTab(tab)}
              disabled={!clickable}
              data-testid={`kpi-${key}`}
              className={`group relative text-left rounded-xl p-3 backdrop-blur-md transition-all duration-300 border ${
                accent
                  ? 'bg-[#DEA900]/12 border-[#DEA900]/40 hover:bg-[#DEA900]/22 hover:border-[#DEA900]/60'
                  : 'bg-white/8 border-white/12 hover:bg-white/14 hover:border-white/25'
              } ${clickable ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default opacity-90'}`}
            >
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                  accent ? 'bg-[#DEA900]/25 group-hover:bg-[#DEA900]/40' : 'bg-white/12 group-hover:bg-white/22'
                }`}>
                  <Ico className={`h-4 w-4 ${accent ? 'text-[#f5cc3e]' : 'text-white'}`} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold leading-none tracking-tight text-white">{val}</p>
                  <p className={`text-[10px] mt-1 uppercase tracking-wider truncate ${accent ? 'text-[#f5cc3e]/90' : 'text-white/55'}`}>{label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* === STATS DÉTAILLÉES — Palette O'SCOP unifiée === */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          {
            key: 'membres', tab: 'gestion-membres-cooper', label: t('admin_dashboard.members', 'Membres'),
            value: stats?.membres?.total || 0, Icon: Users, accent: 'gold',
            sub: [
              { dot: 'bg-emerald-500', text: `${stats?.membres?.valides || 0} validés` },
              { dot: 'bg-amber-500', text: `${stats?.membres?.en_attente || 0} en attente` },
            ],
          },
          {
            key: 'coopers', tab: 'coopers', label: "COOPER'S",
            value: stats?.coopers?.total || 0, Icon: Shield, accent: 'navy', highlight: true,
            sub: [{ dot: 'bg-emerald-500', text: `${stats?.coopers?.valides || 0} validés` }],
          },
          {
            key: 'subs', tab: 'souscriptions-liste', label: t('admin_dashboard.subscriptions', 'Souscriptions'),
            value: stats?.souscriptions?.total || 0, Icon: FileText, accent: 'gold',
            sub: [
              { dot: 'bg-emerald-500', text: `${stats?.souscriptions?.actives || 0} actives` },
              { dot: 'bg-amber-500', text: `${stats?.souscriptions?.en_attente_signature || 0} en attente` },
            ],
          },
          {
            key: 'rcds', tab: 'suivi-rcds', label: 'Suivi RCDS',
            value: stats?.souscriptions?.total || 0, Icon: Shield, accent: 'navy',
            caption: 'Souscriptions & Rappels',
          },
          {
            key: 'dos', tab: 'suivi-dos-domium', label: 'Suivi DOS DOMIUM',
            value: 'DOS', Icon: Building2, accent: 'gold',
            caption: 'Dommage Ouvrage Solidaire',
          },
          {
            key: 'recus', tab: 'virements-ess', label: 'Reçus générés',
            value: (stats?.recus_ess || 0) + (stats?.recus_paiement || 0), Icon: Receipt, accent: 'navy',
            sub: [
              { dot: 'bg-emerald-500', text: `${stats?.recus_ess || 0} ESS` },
              { dot: 'bg-[#DEA900]', text: `${stats?.recus_paiement || 0} Paiement` },
            ],
          },
          {
            key: 'dommages', tab: null, label: t('admin_dashboard.damages', 'Dommages'),
            value: stats?.dommages?.total || 0, Icon: AlertCircle, accent: 'red',
            sub: [{ dot: 'bg-rose-500', text: 'À traiter' }],
          },
        ].map(({ key, tab, label, value, Icon, accent, sub, caption, highlight }) => {
          const palettes = {
            navy: {
              iconBg: 'bg-[#0a3d62]/8 group-hover:bg-[#0a3d62]/14',
              iconColor: 'text-[#0a3d62]',
              valueColor: 'text-[#0a3d62]',
              labelColor: 'text-[#0a3d62]/70',
              ring: 'group-hover:ring-[#0a3d62]/20',
              bar: 'bg-[#0a3d62]',
            },
            gold: {
              iconBg: 'bg-[#DEA900]/12 group-hover:bg-[#DEA900]/20',
              iconColor: 'text-[#b88600]',
              valueColor: 'text-[#0a3d62]',
              labelColor: 'text-[#b88600]',
              ring: 'group-hover:ring-[#DEA900]/30',
              bar: 'bg-[#DEA900]',
            },
            red: {
              iconBg: 'bg-rose-100 group-hover:bg-rose-200',
              iconColor: 'text-rose-600',
              valueColor: 'text-rose-700',
              labelColor: 'text-rose-600',
              ring: 'group-hover:ring-rose-200',
              bar: 'bg-rose-500',
            },
          }[accent];
          const Cmp = tab ? 'button' : 'div';
          return (
            <Cmp
              key={key}
              type={tab ? 'button' : undefined}
              onClick={tab ? () => setActiveTab(tab) : undefined}
              data-testid={`stat-card-${key}`}
              className={`group relative text-left bg-white border border-slate-200/80 rounded-2xl p-5 transition-all duration-300 ring-1 ring-transparent ${palettes.ring} ${
                tab ? 'hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)] cursor-pointer' : 'cursor-default'
              } ${highlight ? 'ring-1 ring-[#DEA900]/35 shadow-[0_8px_24px_-12px_rgba(222,169,0,0.25)]' : ''}`}
            >
              {/* Side bar accent */}
              <span className={`absolute left-0 top-5 bottom-5 w-1 rounded-r-full ${palettes.bar} opacity-80`} />
              <div className="flex items-start gap-4 pl-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${palettes.iconBg}`}>
                  <Icon className={`h-6 w-6 ${palettes.iconColor}`} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-3xl font-bold tracking-tight tabular-nums ${palettes.valueColor}`}>{value}</div>
                  <div className={`text-xs font-semibold uppercase tracking-wider mt-0.5 ${palettes.labelColor}`}>{label}</div>
                  {caption && <div className="text-[11px] text-slate-500 mt-1">{caption}</div>}
                  {sub && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-600">
                      {sub.map((s, i) => (
                        <span key={i} className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                          {s.text}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Cmp>
          );
        })}
      </div>
    </>
  );
}
